import styled from "@emotion/styled";
import * as _ from "lodash";
import React, { BaseSyntheticEvent } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import DashboardCreateModal from ".";
import { useCreateDashboardStore } from "..";
import { GatewayText_s, GatewayTexts, NetworkOBJ } from "constant/gateway";
import { Registry } from "types";

export enum SelectionMode {
    BOTH = "both",
    GATEWAY_ONLY = "gateway",
    NETWORK_ONLY = "network",
}

type SelectGatewayModalProps = {
    show: boolean;
    onHide: () => void;
    values: Record<string, Registry> | undefined;
    onSave: (
        event: Event | BaseSyntheticEvent,
        data: Record<string, Registry>
    ) => void;
    selectionMode?: SelectionMode;
};

const StyledDashboardCreateModal = styled(DashboardCreateModal)`
    .modal-filter .modal-option {
        flex: 0 0 160px;
        margin-right: 0.5rem;
        padding: 0;
    }

    .modal-filter .modal-option .custom-control-label:before,
    .modal-filter .modal-option .custom-control-label:after {
        top: 50%;
        transform: translateY(-50%);
    }
`;

const DashboardCreateSelectGatewayModal = (props: SelectGatewayModalProps) => {
    const { allRegistry } = useCreateDashboardStore();
    const [filteredRegistry, setFilteredRegistry] = React.useState<any[]>([]);
    const [searchString, setSearchString] = React.useState("");
    const { show, onHide, onSave, selectionMode = SelectionMode.GATEWAY_ONLY } = props;
    const [values, setValues] = React.useState<any>({});
    const filteredOnboardedRegistry = filteredRegistry.filter((r) => r.info);
    const allOnboardedRegistry = allRegistry.filter((r) => r.info);

    // Get filtered registry based on selection mode for "Select All" functionality
    const getFilteredRegistryForSelectAll = () => {
        const registry = searchString ? filteredOnboardedRegistry : allOnboardedRegistry;

        switch (selectionMode) {
            case SelectionMode.GATEWAY_ONLY:
                return registry.filter((r) => r.info?.OBJ !== NetworkOBJ);
            case SelectionMode.NETWORK_ONLY:
                return registry.filter((r) => r.info?.OBJ === NetworkOBJ);
            case SelectionMode.BOTH:
            default:
                return registry;
        }
    };

    // Get title based on selection mode
    const getTitle = () => {
        switch (selectionMode) {
            case SelectionMode.GATEWAY_ONLY:
                return "Select Gateways";
            case SelectionMode.NETWORK_ONLY:
                return "Select Networks";
            case SelectionMode.BOTH:
            default:
                return `Select ${GatewayTexts}`;
        }
    };

    // Get footer text based on selection mode
    const getFooterText = (count: number) => {
        switch (selectionMode) {
            case SelectionMode.GATEWAY_ONLY:
                return `${count} Gateway(s) are selected`;
            case SelectionMode.NETWORK_ONLY:
                return `${count} Network(s) are selected`;
            case SelectionMode.BOTH:
            default:
                return `${count} ${GatewayText_s} are selected`;
        }
    };

    // Render empty message
    const renderEmptyMessage = () => {
        switch (selectionMode) {
            case SelectionMode.GATEWAY_ONLY:
                return "No gateways to display";
            case SelectionMode.NETWORK_ONLY:
                return "No networks to display";
            case SelectionMode.BOTH:
            default:
                return `No ${GatewayTexts} to display`;
        }
    };

    React.useEffect(() => {
        if (searchString) {
            setFilteredRegistry(
                allRegistry.filter((item) =>
                    item.name
                        .toLowerCase()
                        .includes(String(searchString).trim().toLowerCase())
                )
            );
        } else {
            setFilteredRegistry(allRegistry);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString]);

    React.useEffect(() => {
        if (show) {
            // When value is a string list
            if (Array.isArray(props.values)) {
                const selectedGateways = {} as any;
                props.values.forEach((value: any) => {
                    _.set(
                        selectedGateways,
                        value,
                        allRegistry.find((item) => item.name === value)
                    );
                });

                setValues(selectedGateways);
            } else {
                setValues(props.values || {});
            }

            setFilteredRegistry(allRegistry);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const renderFooter = () => {
        const count = Object.keys(values).length;
        return (
            <div className="row w-100 justify-content-between align-items-center">
                <strong className="text-gray">
                    {!!count && getFooterText(count)}
                </strong>
                <Button
                    variant="primary"
                    onClick={(event) => {
                        onSave?.(event, values);
                        onHide?.();
                    }}
                >
                    ADD
                </Button>
            </div>
        );
    };

    const renderGateway = () => {
        if (allRegistry.length <= 0) {
            return (
                <p className="w-100 d-flex justify-content-center">
                    {renderEmptyMessage()}
                </p>
            );
        }

        const registryToDisplay = searchString ? filteredRegistry : allRegistry;

        // Filter based on selection mode
        let PSLNetworkList: any[] = [];
        let IOTPGatewayList: any[] = [];

        if (selectionMode === SelectionMode.BOTH) {
            PSLNetworkList = registryToDisplay.filter((registry) => registry.info?.OBJ === NetworkOBJ);
            IOTPGatewayList = registryToDisplay.filter((registry) => registry.info?.OBJ !== NetworkOBJ);
        } else if (selectionMode === SelectionMode.NETWORK_ONLY) {
            PSLNetworkList = registryToDisplay.filter((registry) => registry.info?.OBJ === NetworkOBJ);
        } else if (selectionMode === SelectionMode.GATEWAY_ONLY) {
            IOTPGatewayList = registryToDisplay.filter((registry) => registry.info?.OBJ !== NetworkOBJ);
        }

        const groupedRegistry = _.groupBy(IOTPGatewayList, "groupName") || {};
        const groupNames = Object.keys(groupedRegistry);

        const renderGatewayCheckbox = (subDevice: any) => (
            <div className="modal-option" key={subDevice.gateway_id}>
                <Form.Check
                    name="selected-gateway"
                    value={`${subDevice.name}id:${subDevice.gateway_id}`}
                    checked={values?.hasOwnProperty(subDevice.gateway_id)}
                    disabled={!subDevice.info}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setValues({
                                ...values,
                                [subDevice.gateway_id]: subDevice,
                            });
                        } else {
                            setValues(_.omit(values, subDevice.gateway_id));
                        }
                    }}
                    id={"group-gateway" + subDevice.gateway_id}
                    custom
                    type="checkbox"
                    label={
                        <div>
                            <h4>{subDevice.name}</h4>{" "}
                            <p>UUID: {subDevice.gateway_id}</p>
                        </div>
                    }
                    className="float-left"
                />
            </div>
        );

        return (
            <>
                {(selectionMode === SelectionMode.BOTH || selectionMode === SelectionMode.GATEWAY_ONLY) &&
                    groupNames.map((name: string) => {
                        const group = _.get(groupedRegistry, name, []);

                        return (
                            <div key={name} className="d-block relative w-100 mb-3">
                                <h4 className="mb-0">{name}</h4>
                                {group.map(renderGatewayCheckbox)}
                            </div>
                        );
                    })
                }
                {(selectionMode === SelectionMode.BOTH || selectionMode === SelectionMode.NETWORK_ONLY) &&
                    PSLNetworkList.length > 0 && (
                        <div className="d-block relative w-100 mb-3">
                            <h4 className="mb-0">Networks</h4>
                            {PSLNetworkList.map(renderGatewayCheckbox)}
                        </div>
                    )
                }
            </>
        );
    };

    return (
        <StyledDashboardCreateModal
            title={getTitle()}
            show={show}
            onHide={() => onHide?.()}
            footer={renderFooter}
        >
            <Form className="d-flex flex-wrap">
                <div className="d-flex align-items-center w-100 mb-4 modal-filter">
                    <div className="modal-option">
                        <Form.Check
                            id="gateway-check-all"
                            type="checkbox"
                            className="text-gray"
                            label="Select All"
                            custom
                            checked={
                                !!Object.keys(values).length &&
                                Object.keys(values).length ===
                                _.union(
                                    Object.keys(values),
                                    Object.keys(
                                        _.keyBy(
                                            getFilteredRegistryForSelectAll(),
                                            "gateway_id"
                                        )
                                    )
                                ).length
                            }
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setValues(
                                        _.keyBy(
                                            getFilteredRegistryForSelectAll(),
                                            "gateway_id"
                                        )
                                    );
                                } else {
                                    setValues({});
                                }
                            }}
                        />
                    </div>
                    <Form.Control
                        type="text"
                        placeholder="Search"
                        onChange={(e) => setSearchString(e.target.value)}
                    ></Form.Control>
                </div>

                {renderGateway()}
            </Form>
        </StyledDashboardCreateModal>
    );
};

export default DashboardCreateSelectGatewayModal;
